import { makeStyles } from '@material-ui/core/styles'

const FIELD_HEIGHT = 48
const FIELD_RADIUS = 12
const LABEL_LINE_HEIGHT = 20
const CARD_BORDER = '#dfe3ea'
const OPTION_HOVER_FILL = '#F5F7FA'
/** Card inner width where first/last name fit side by side. */
const NAME_PAIR_MIN_WIDTH = 300

export const useSidebarStyles = makeStyles((theme) => ({
  /** Same card chrome as the Book a lesson widget in the sidebar. */
  root: {
    width: '100%',
    boxSizing: 'border-box',
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: 16,
    padding: theme.spacing(2.5),
    backgroundColor: theme.palette.common.white,
    boxShadow: '0 8px 24px rgba(15, 34, 52, 0.06)',
    containerType: 'inline-size',
  },
  header: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  heading: {
    margin: 0,
    color: theme.palette.secondary.dark,
    fontWeight: 700,
    fontSize: '1.25rem',
    lineHeight: 1.3,
    '&:focus:not(:focus-visible)': {
      outline: 'none',
    },
  },
  stepLabel: {
    margin: 0,
    flexShrink: 0,
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: theme.palette.grey.darker,
  },
  progress: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  progressSegment: {
    height: 4,
    borderRadius: 2,
    backgroundColor: CARD_BORDER,
    transition: 'background-color 200ms ease',
  },
  progressSegmentActive: {
    backgroundColor: theme.palette.blue.main,
  },
  step: {
    '&[hidden]': {
      display: 'none',
    },
  },
  subtitle: {
    ...theme.typography.body2,
    margin: 0,
    marginBottom: theme.spacing(2),
    color: theme.palette.grey.darker,
  },
  fieldset: {
    margin: 0,
    padding: 0,
    border: 0,
    minWidth: 0,
  },
  question: {
    padding: 0,
    marginBottom: theme.spacing(1.5),
    fontSize: '1rem',
    fontWeight: 700,
    lineHeight: 1.35,
    color: theme.palette.secondary.dark,
  },
  intentList: {
    display: 'grid',
    gap: theme.spacing(1),
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  intentOption: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    width: '100%',
    minHeight: 44,
    padding: theme.spacing(1.25, 1.75),
    boxSizing: 'border-box',
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: FIELD_RADIUS,
    backgroundColor: theme.palette.common.white,
    color: theme.palette.secondary.dark,
    fontFamily: 'inherit',
    fontSize: '0.9375rem',
    fontWeight: 600,
    lineHeight: 1.3,
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'border-color 150ms ease, background-color 150ms ease',
    '& svg': {
      flexShrink: 0,
      color: theme.palette.blue.main,
    },
    '&:hover': {
      borderColor: theme.palette.blue.main,
      backgroundColor: OPTION_HOVER_FILL,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.blue.main}`,
      outlineOffset: 2,
    },
  },
  intentOptionSelected: {
    borderColor: theme.palette.blue.main,
    boxShadow: `inset 0 0 0 1px ${theme.palette.blue.main}`,
  },
  selection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.25, 1.75),
    borderRadius: FIELD_RADIUS,
    backgroundColor: OPTION_HOVER_FILL,
  },
  selectionText: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  selectionLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: theme.palette.grey.darker,
  },
  selectionValue: {
    fontSize: '0.9375rem',
    fontWeight: 700,
    lineHeight: 1.3,
    color: theme.palette.secondary.dark,
  },
  changeButton: {
    flexShrink: 0,
    minWidth: 0,
    padding: theme.spacing(0.5),
    color: theme.palette.blue.main,
    fontSize: '0.875rem',
    fontWeight: 700,
    lineHeight: 1.3,
    letterSpacing: 0,
    textTransform: 'none',
    textDecoration: 'underline',
    '&:hover': {
      background: 'none',
      textDecoration: 'underline',
    },
    '&.Mui-focusVisible': {
      outline: `2px solid ${theme.palette.blue.main}`,
      outlineOffset: 2,
    },
  },
  fields: {
    display: 'grid',
    gap: theme.spacing(1.5),
  },
  nameRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: theme.spacing(1.5),
    [`@container (min-width: ${NAME_PAIR_MIN_WIDTH}px)`]: {
      gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
      gap: theme.spacing(1),
    },
  },
  field: {
    '& .MuiOutlinedInput-root': {
      height: FIELD_HEIGHT,
      borderRadius: FIELD_RADIUS,
      backgroundColor: theme.palette.common.white,
    },
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
      height: FIELD_HEIGHT,
      padding: `0 ${theme.spacing(1.75)}px !important`,
    },
    '& .MuiOutlinedInput-input, & .MuiAutocomplete-input': {
      boxSizing: 'border-box',
      height: '100%',
      padding: `0 ${theme.spacing(1.75)}px`,
      fontSize: '1rem',
    },
    '& .MuiAutocomplete-input': {
      padding: '0 !important',
    },
    /** Fixed offset (not `top: 50%`) so helper text below an errored field cannot shift the label. */
    '& .MuiInputLabel-outlined': {
      lineHeight: `${LABEL_LINE_HEIGHT}px`,
      '&:not(.MuiInputLabel-shrink)': {
        transform: `translate(${theme.spacing(1.75)}px, ${(FIELD_HEIGHT - LABEL_LINE_HEIGHT) / 2}px)`,
      },
      '&.MuiInputLabel-shrink': {
        transform: `translate(${theme.spacing(1.75)}px, -${(LABEL_LINE_HEIGHT * 0.75) / 2}px) scale(0.75)`,
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: CARD_BORDER,
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(15, 50, 84, 0.32)',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderWidth: 2,
      borderColor: theme.palette.blue.main,
    },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.error.main,
    },
    '& .MuiFormHelperText-contained': {
      marginLeft: theme.spacing(0.5),
      marginRight: 0,
    },
  },
  recaptcha: {
    marginTop: theme.spacing(1),
    lineHeight: 0,
  },
  recaptchaExpired: {
    marginTop: 0,
    lineHeight: 1.4,
  },
  termsNotice: {
    '&&': {
      marginTop: theme.spacing(1.5),
      lineHeight: 1.5,
    },
  },
  submitError: {
    marginTop: theme.spacing(1),
    lineHeight: 1.4,
  },
  submit: {
    height: FIELD_HEIGHT,
    marginTop: theme.spacing(1.5),
    borderRadius: 999,
    fontWeight: 700,
    textTransform: 'none',
    fontSize: '0.9375rem',
  },
  success: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2, 0),
    textAlign: 'center',
    '& $subtitle': {
      marginBottom: 0,
    },
  },
  successIcon: {
    color: theme.palette.blue.main,
  },
}))
